import React, { useState, useEffect, useRef } from 'react';
import { collection, getDocs, doc, setDoc, getDoc, updateDoc, deleteDoc, addDoc, deleteField, runTransaction } from "firebase/firestore";
import { Save, ArrowLeft, User, MapPin, Plus, Trash2, X, Edit2, Phone, Mail, FileText, UserCheck, Copy, ChevronDown, CheckCircle } from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import useClickOutside from './hooks/useClickOutside.js';
import useUnsavedChanges from './hooks/useUnsavedChanges.js';
import Spinner from './Spinner.js';
import { NewCustomerModal, AddAddressModal } from './CustomerModals.js';
import { useConfig } from './hooks/useConfig.js';
import { canAccessQuotes } from './accessControl.js';
import { buildQuoteAuditFields } from './utils/quoteAudit.js';

// --- HELPER: Format Address Object to String ---
const formatAddress = (addr) => {
  if (!addr) return 'No Address Selected';
  return `${addr.address || 'N/A'}, ${addr.city || 'N/A'}, ${addr.state || 'N/A'} - ${addr.pincode || 'N/A'}`;
};

const getFirmPrefix = (firmId) => { 
  if (firmId.startsWith('nexrise')) return 'NIIPL'; 
  if (firmId.startsWith('jagdish')) return 'JE';     
  if (firmId.startsWith('cloudnine')) return 'C9';   
  return 'GEN'; 
};

const getDateAfterDays = (dateString, days = 30) => {
  if (!dateString) return '';
  const date = new Date(`${dateString}T00:00:00`);
  if (Number.isNaN(date.getTime())) return '';
  date.setDate(date.getDate() + days);
  return date.toISOString().split('T')[0];
};

const normalizeLeadSource = (value) => {
  if (value === 'Word of Mouth') return 'Customer Referral';
  return value || '';
};

const formatDisplayDateTime = (value) => {
  if (!value) return 'N/A';

  const date = value?.toDate ? value.toDate() : new Date(value);
  if (Number.isNaN(date.getTime())) return 'N/A';

  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const leadSourceOptions = [
  'Indiamart',
  'Tradeindia',
  'Website',
  'Existing Customer',
  'Customer Referral',
  'Search Engine (Google, YouTube etc.)',
  'Social Media',
  'Other',
];

const QuoteDetail = ({ db, user }) => {
  const { config } = useConfig();
  const { id } = useParams(); // Get quote ID from URL
  const navigate = useNavigate();
  const customerSearchRef = useClickOutside(() => setShowCustSuggestions(false));
  const dropdownRef = useClickOutside(() => setShowDropdown(false));

  const isAdmin = user?.role === 'admin';

  // --- RESPONSIVE STATE ---
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const isMobile = windowWidth <= 768;

  // --- 1. MAIN FORM STATE ---
  const today = new Date().toISOString().split('T')[0];
  const [formData, setFormData] = useState({
    firmId: '',
    date: today,
    expiryDate: getDateAfterDays(today, 30),
    quoteType: 'Sales',
    salesPerson: '',
    leadSource: '',
    subject: '',
    customerID: '',
    billingAddress: null,
    shippingAddress: null,
    quoteNumber: '',
    status: 'draft', 
  });

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const isInitialRender = useRef(true);
  const originalQuoteData = useRef(null);
  const [showDropdown, setShowDropdown] = useState(false);

  const setSafeToNavigate = useUnsavedChanges(isDirty);

  // --- 2. DATA LOAD STATE ---
  const [firms, setFirms] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [allCustomers, setAllCustomers] = useState([]);
  const [termsText, setTermsText] = useState('');

  // Track changes to form data in edit mode
  useEffect(() => {
    if (isEditing) {
      if (isInitialRender.current) {
        isInitialRender.current = false;
        return;
      }
      setIsDirty(true);
    } else {
      setIsDirty(false);
      isInitialRender.current = true;
    }
  }, [isEditing, formData, items, termsText]);

  
  // --- 3. UI STATE ---
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [showCustSuggestions, setShowCustSuggestions] = useState(false);
  const [activeSearchRow, setActiveSearchRow] = useState(null); 
  const [filteredProducts, setFilteredProducts] = useState([]);
  
  // --- 4. MODAL STATES ---
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [addressModalType, setAddressModalType] = useState('billing'); 
  const [showModal, setShowModal] = useState(false); 
  const [customerDataToEdit, setCustomerDataToEdit] = useState(null); 
  const [addressDataToEdit, setAddressDataToEdit] = useState(null); 
  
  const calculateSubTotal = () => items.reduce((sum, item) => {
    const base = (item.qty || 0) * (item.rate || 0);
    return sum + (base - (base * ((item.discount || 0) / 100)));
  }, 0);

  const calculateGSTTotal = () => items.reduce((sum, item) => {
    const base = (item.qty || 0) * (item.rate || 0);
    const val = base - (base * ((item.discount || 0) / 100));
    return sum + (val * ((item.tax || 0) / 100));
  }, 0);

  const getGrandTotal = () => calculateSubTotal() + calculateGSTTotal();

  // --- INITIAL DATA LOADING ---
  useEffect(() => {
    const loadQuoteAndAllData = async () => {
      setLoading(true);
      try {
        const fetches = [
          getDocs(collection(db, "firms")),
          getDocs(collection(db, "customers"))
        ];

        if (canAccessQuotes(user)) {
          fetches.push(getDocs(collection(db, "products")));
        }

        const [firmSnap, custSnap, prodSnap] = await Promise.all(fetches);

        const firmsList = firmSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setFirms(firmsList);

        const customersList = custSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setAllCustomers(customersList);
        
        const productsList = prodSnap?.docs?.map(doc => ({ id: doc.id, ...doc.data() })) || [];
        setAllProducts(productsList);

        if (id) {
          const quoteRef = doc(db, "quotes", id);
          const quoteSnap = await getDoc(quoteRef);
          if (quoteSnap.exists()) {
            const quoteData = quoteSnap.data();
            originalQuoteData.current = { ...quoteData, id: quoteSnap.id };
            
            setFormData({
              firmId: quoteData.firmId || firmsList[0]?.id || 'N/A',
              date: quoteData.date,
              expiryDate: quoteData.expiryDate || getDateAfterDays(quoteData.date, 30),
              quoteType: quoteData.quoteType,
              salesPerson: quoteData.salesPerson,
              leadSource: normalizeLeadSource(quoteData.leadSource),
              subject: quoteData.subject,
              customerID: quoteData.customerID,
              billingAddress: quoteData.billingAddress,
              shippingAddress: quoteData.shippingAddress,
              quoteNumber: quoteData.quoteNumber,
              status: quoteData.status || 'draft',
            });
            setItems(quoteData.items || []);
            setTermsText(quoteData.terms ? quoteData.terms.join('\n') : '');

            const currentCustomer = customersList.find(c => c.id === quoteData.customerID);
            if (currentCustomer) {
              setCustomerSearchTerm(currentCustomer.display_name);
            }
          } else {
            alert("Quote not found!");
            navigate('/quotes'); 
          }
        }
      } catch (error) {
        console.error("Failed to load data:", error);
        alert("Failed to load data: " + error.message);
      } finally {
        setLoading(false);
      }
    };
    
    loadQuoteAndAllData();
  }, [db, id, navigate, isAdmin, user?.role, user?.uid]);

  // --- CUSTOMER SEARCH LOGIC ---
  const handleCustomerSearch = (value) => {
    setCustomerSearchTerm(value);
    if (value.length > 0) {
      const term = value.toLowerCase();
      const matches = allCustomers.filter(c => c.display_name?.toLowerCase().includes(term));
      setFilteredCustomers(matches);
      setShowCustSuggestions(true);
    } else {
      setFilteredCustomers(allCustomers);
      setShowCustSuggestions(true);
    }
  };

  const handleFirmChange = (e) => {
    const firmId = e.target.value;
    const originalFirmId = originalQuoteData.current?.firmId;
    
    let newQuoteNumber = formData.quoteNumber;
    if (firmId !== originalFirmId) {
        const newPrefix = getFirmPrefix(firmId);
        newQuoteNumber = `${newPrefix}/25-26/QT---`;
    } else {
        newQuoteNumber = originalQuoteData.current?.quoteNumber;
    }

    setFormData(p => ({ ...p, firmId, quoteNumber: newQuoteNumber }));
    const selectedFirm = firms.find(f => f.id === firmId);
    if (selectedFirm?.terms) {
        setTermsText(selectedFirm.terms.join('\n')); 
    } else {
        setTermsText("1. Payment terms: 100% advance.\n2. Delivery in 4-5 days.\n3. Taxes extra (@18%).");
    }
  };

  const handleDateChange = (value) => {
    setFormData((prev) => {
      const previousDefaultExpiry = getDateAfterDays(prev.date, 30);
      const nextDefaultExpiry = getDateAfterDays(value, 30);
      const shouldSyncExpiry = !prev.expiryDate || prev.expiryDate === previousDefaultExpiry;

      return {
        ...prev,
        date: value,
        expiryDate: shouldSyncExpiry ? nextDefaultExpiry : prev.expiryDate,
      };
    });
  };

  const selectCustomer = (customer) => {
    setFormData(prev => ({
      ...prev, 
      customerID: customer.id,
      billingAddress: customer.addresses?.find(a => a.address_type === 'Billing') || null,
      shippingAddress: customer.addresses?.find(a => a.address_type === 'Shipping') || null,
    }));
    setCustomerSearchTerm(customer.display_name);
    setShowCustSuggestions(false);
  };
  
  const selectedCustomer = allCustomers.find(c => c.id === formData.customerID);
  const isQuoteInvoiced = formData.status === 'invoiced';
  const isEditingAllowed = !isQuoteInvoiced;
  const moreInfoCreatedAt = formatDisplayDateTime(originalQuoteData.current?.createdAt);
  const moreInfoCreatedBy = originalQuoteData.current?.createdBy || 'N/A';
  const moreInfoUpdatedAt = formatDisplayDateTime(originalQuoteData.current?.updatedAt);
  const moreInfoUpdatedBy = originalQuoteData.current?.updatedBy || 'N/A';
  const moreInfoLeadSource = formData.leadSource || 'N/A';

  const handleAddressSelect = (type, value) => {
    if (value === 'new') {
      setAddressModalType(type);
      setShowAddressModal(true);
      return;
    }
    const address = selectedCustomer.addresses.find(a => a.id === value);
    if (type === 'billing') {
      setFormData(prev => ({ ...prev, billingAddress: address }));
    } else {
      setFormData(prev => ({ ...prev, shippingAddress: address }));
    }
  };

  const numberToWords = (num) => {
    const a = [
        '', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
        'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'
    ];
    const b = [
        '', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'
    ];
    const units = [
        { value: 10000000, str: 'Crore' },
        { value: 100000, str: 'Lakh' },
        { value: 1000, str: 'Thousand' },
        { value: 100, str: 'Hundred' }
    ];

    function convertNumberToWords(n) {
        let words = '';
        for (let i = 0; i < units.length; i++) {
            if (Math.floor(n / units[i].value) > 0) {
                words += convertNumberToWords(Math.floor(n / units[i].value)) + ' ' + units[i].str + ' ';
                n %= units[i].value;
            }
        }
        if (n > 19) {
            words += b[Math.floor(n / 10)] + ' ' + a[n % 10] + ' ';
        } 
        else if (n > 0) {
            words += a[n] + ' ';
        }
        return words.trim();
    }

    if (num === 0) return 'Zero Rupees Only';
    let rupees = Math.floor(num);
    let paisa = num % 1;
    let result = convertNumberToWords(rupees) + ' Rupees';
    if (paisa > 0) {
        paisa = (paisa * 100).toFixed(0);
        result += ' and ' + convertNumberToWords(parseInt(paisa)) + ' Paise';
    }
    return result + ' Only';
  };

  // --- ACTION FUNCTIONS ---
  const handleMarkAsSent = async () => {
    const quoteRef = doc(db, "quotes", id);
    try {
      const timestamp = new Date().toISOString();
      await updateDoc(quoteRef, {
        status: 'sent',
        sentAt: timestamp,
        ...buildQuoteAuditFields(user, timestamp)
      });
      setFormData(prev => ({ ...prev, status: 'sent' }));
      alert('Quote marked as Sent!');
    } catch (error) {
      console.error("Error marking quote as sent: ", error);
      alert("Failed to mark quote as sent: " + error.message);
    }
  };

  const handleUndoSent = async () => {
    const quoteRef = doc(db, "quotes", id);
    try {
      const timestamp = new Date().toISOString();
      await updateDoc(quoteRef, {
        status: 'draft',
        sentAt: deleteField(),
        ...buildQuoteAuditFields(user, timestamp)
      });
      setFormData(prev => ({ ...prev, status: 'draft' }));
      alert('Quote status reverted to Draft.');
    } catch (error) {
      console.error("Error undoing sent status: ", error);
      alert("Failed to undo sent status: " + error.message);
    }
  };

  const updateQuote = async () => {
    const requiredFields = {
      firmId: 'From (Firm)',
      date: 'Date',
      expiryDate: 'Expiry Date',
      salesPerson: 'Salesperson',
      quoteType: 'Quote Type',
      customerID: 'Customer',
      billingAddress: 'Billing Address',
      subject: 'Subject'
    };

    const missingFieldKeys = Object.keys(requiredFields).filter(field => {
      const value = formData[field];
      return !value || (typeof value === 'string' && value.trim() === '');
    });

    if (missingFieldKeys.length > 0) {
      const missingFieldNames = missingFieldKeys.map(key => requiredFields[key]).join(', ');
      alert(`Please fill out all required fields: ${missingFieldNames}.`);
      return;
    }

    if (items.length === 0 || (items.length === 1 && !items[0].name)) {
      alert('Please add at least one item to the quote.');
      return;
    }

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (!item.name || (item.qty || 0) <= 0 || (item.rate || 0) <= 0) {
        alert(`Please provide a valid Name, positive Quantity, and positive Rate for item #${i + 1}.`);
        return;
      }
    }

    setIsSaving(true);
    const quoteRef = doc(db, "quotes", id);

    try {
        const timestamp = new Date().toISOString();
        let finalQuoteNumber = formData.quoteNumber;
        
        // If firm changed, generate new sequence number
        if (formData.firmId !== originalQuoteData.current?.firmId) {
            const firmPrefix = getFirmPrefix(formData.firmId);
            const counterKey = firmPrefix.toLowerCase() + '_count';
            const sequencesRef = doc(db, "counters", "sequences");
            const yearCode = "25-26";

            const newCounter = await runTransaction(db, async (transaction) => {
                const sequenceDoc = await transaction.get(sequencesRef);
                if (!sequenceDoc.exists()) throw new Error("Counter document does not exist!");
                const currentCount = sequenceDoc.data()[counterKey] || 1000;
                const nextCount = currentCount + 1;
                transaction.update(sequencesRef, { [counterKey]: nextCount });
                return currentCount;
            });
            finalQuoteNumber = `${firmPrefix}/${yearCode}/QT${newCounter}`;
        }

        const quotePayload = {
            ...formData,
            quoteNumber: finalQuoteNumber,
            customerName: selectedCustomer.display_name, 
            billingAddress: formData.billingAddress,     
            terms: termsText.split('\n').filter(t => t.trim() !== ''),
            shippingAddress: formData.shippingAddress || formData.billingAddress,
            items: items,
            grandTotal: getGrandTotal(),
            ...buildQuoteAuditFields(user, timestamp)
        };

        await updateDoc(quoteRef, quotePayload);
        
        alert(`Quote ${finalQuoteNumber} Updated Successfully!`);
        originalQuoteData.current = {
          ...originalQuoteData.current,
          ...quotePayload,
          id
        }; // Preserve creation metadata while refreshing the latest saved values
        setFormData(prev => ({ ...prev, quoteNumber: finalQuoteNumber }));
        setIsDirty(false);
        setSafeToNavigate();
        setIsEditing(false); 
        setIsSaving(false);
    } catch (error) {
        setIsSaving(false);
        console.error("Error updating quote: ", error);
        alert("Error updating quote: " + error.message);
    }
  };

  const handleClone = () => {
    const clonedQuote = { ...formData, items, terms: termsText.split('\n').filter(t => t.trim() !== '') };
    delete clonedQuote.id;
    delete clonedQuote.quoteNumber;
    navigate('/quotes/new', { state: { clonedQuote } });
  };

  const handleConvertToInvoice = async () => {
    const confirmMessage = "Are you sure you want to convert this Quote to an Invoice? \n\nThis will mark the quote as 'Invoiced' and open the new invoice form with these details.";
    if (!window.confirm(confirmMessage)) return;

    const quoteRef = doc(db, "quotes", id);
    try {
      const timestamp = new Date().toISOString();
      await updateDoc(quoteRef, { status: 'invoiced', ...buildQuoteAuditFields(user, timestamp) });
      const convertedQuote = { ...formData, items, terms: termsText.split('\n').filter(t => t.trim() !== '') };
      navigate('/invoices/new', { state: { convertedQuote } });
    } catch (error) {
      console.error("Error updating quote status: ", error);
      alert("Failed to update quote status.");
    }
  };

  const handleDelete = async () => {
    if (window.confirm(`Are you sure you want to delete Quote #${formData.quoteNumber}? This action cannot be undone.`)) {
      try {
        await deleteDoc(doc(db, "quotes", id));
        alert('Quote deleted successfully.');
        navigate('/quotes');
      } catch (error) {
        console.error("Error deleting quote: ", error);
        alert('Failed to delete quote: ' + error.message);
      }
    }
  };

  const handleExportPdf = () => {
    const selectedFirm = firms.find(f => f.id === formData.firmId);
    if (!selectedFirm) {
        alert('Could not find firm details for this quote.');
        return;
    }

    let subTotal = 0;
    const itemsHtml = items.map((item, index) => {
        const itemAmount = (item.qty || 0) * (item.rate || 0);
        const discountAmount = itemAmount * ((item.discount || 0) / 100);
        const netAmount = itemAmount - discountAmount;
        subTotal += netAmount;

        const descriptionWithLineBreaks = (item.description || 'N/A').replace(/\n/g, '<br />');
        const finalDescription = `<strong>${item.name}</strong><br />${descriptionWithLineBreaks}`;

        return `
            <tr class="item-row">
                <td>${index + 1}</td>
                <td>${item.sku || 'N/A'}</td>
                <td contenteditable="true">${finalDescription}</td>
                <td>${item.hsn || 'N/A'}</td>
                <td class="text-right">${item.qty || 0}</td>
                <td class="text-right">${(item.rate || 0).toFixed(2)}</td>
                <td class="text-right">${discountAmount.toFixed(2)}</td> 
                <td class="text-right">${netAmount.toFixed(2)}</td>
            </tr>
        `;
    }).join('');

    const gstAmount = subTotal * 0.18;
    const totalAmount = subTotal + gstAmount;
    const formattedQuoteDate = new Date(formData.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    const expiryDate = formData.expiryDate || getDateAfterDays(formData.date, 30);
    const formattedExpiryDate = expiryDate
      ? new Date(`${expiryDate}T00:00:00`).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
      : 'N/A';
    const termsHtml = `<ol>${termsText.split('\n').filter(t => t.trim() !== '').map(term => `<li>${term}</li>`).join('')}</ol>`;

    const embeddedCss = `
        .container { width: 1000px; margin: 0 auto; font-family: Arial, sans-serif; }
        table { width: 100%; border-collapse: collapse; }
        .header-table td { padding: 10px; vertical-align: middle; }
        .header-table{ border: solid 1px #1b1b1b; }
        .header-table p{ font-size:30px; font-weight: bold; }
        .logo { border-right:1px solid #1b1b1b; }
        .firm-details{ font-size:12px; }
        .firm-details strong{ font-size: 20px; }
        .Upper-table td { padding: 5px; text-align: left; width:25% }
        .Upper-table{ border-right: solid 1px #1b1b1b; border-left: solid 1px #1b1b1b; }
        .table-titles { background-color: #f2f2f2; border-bottom: 1px solid #1b1b1b; border-top: 1px solid #1b1b1b; font-weight: bold; }
        .item-table th, .item-table td { border: solid 1px #1b1b1b; padding: 5px; vertical-align: top; }
        .item-table th { background-color: #f9f9f9; text-align: left; }
        .text-right { text-align: right; }
        .tnc-table{ border-left: solid 1px #1b1b1b; border-right: solid 1px #1b1b1b; border-bottom: solid 1px #1b1b1b; }
        .terms-conditions { width: 70%; padding:10px; font-size: 12px; }
        .signature-area{ width: 30%; text-align:right; padding:10px; vertical-align: bottom; }
        table.Upper-table tr td:nth-child(2){border-right: solid 1px black}
        .doc-title p{margin: 5px}
    `;

    const quoteHtml = `
      <!DOCTYPE html><html><head><title>Quotation ${formData.quoteNumber}_${selectedCustomer?.display_name}</title><style>${embeddedCss}</style></head>
      <body>
          <div class="container">
            <table class="header-table">
                <tr>
                    <td class="logo" style="width: 20%;"><img width="150px" src="${selectedFirm.logo}" alt="${selectedFirm.name} Logo"></td>
                    <td class="firm-details">
                        <strong>${selectedFirm.name}</strong>,<br>
                        ${selectedFirm.address}<br>
                        GSTIN - ${selectedFirm.gstin}<br>
                        E-Mail - ${selectedFirm.email}<br>
                        Tel: ${selectedFirm.tel}
                    </td>
                    <td style="text-align: right; vertical-align: top;" class="doc-title"><p>QUOTE</p>${formData.quoteNumber}</td>
                </tr>
            </table>
            <table class="Upper-table">
              <tr>
                  <td><strong>Date:</strong></td><td>${formattedQuoteDate}</td>
                  <td><strong>Place of supply:</strong></td><td>${formData.shippingAddress?.state || 'N/A'}</td>
              </tr>
              <tr>
                  <td><strong>Expiry Date:</strong></td><td>${formattedExpiryDate}</td>
                  <td><strong>Salesperson</strong></td><td>${formData.salesPerson}</td>
              </tr>
              <tr class="table-titles">
                  <td colspan="2" style="border-right:1px solid #1b1b1b;"><strong>Billing Details:</strong></td>
                  <td colspan="2"><strong>Shipping Details:</strong></td>
              </tr>
              <tr>
                  <td><strong>Customer</strong></td><td>${selectedCustomer?.display_name}</td>
                  <td><strong>Ship to:</strong></td><td>${formData.shippingAddress?.attention || selectedCustomer?.display_name}</td>
              </tr>
              <tr>
                  <td><strong>Address</strong></td><td>${formatAddress(formData.billingAddress)}</td>   
                  <td><strong>Address</strong></td><td>${formatAddress(formData.shippingAddress)}</td>
              </tr>
              <tr>
                  <td><strong>Contact Person</strong></td><td>${formData.billingAddress?.attention || 'N/A'}</td>
                  <td><strong>Contact Person</strong></td><td>${formData.shippingAddress?.attention || 'N/A'}</td>
              </tr>
              <tr>
                  <td><strong>Contact Number</strong></td><td>${formData.billingAddress?.phone || 'N/A'}</td>
                  <td><strong>Contact Number</strong></td><td>${formData.shippingAddress?.phone || 'N/A'}</td>
              </tr>
              
              <tr class="table-titles"><td colspan="4"><strong>Subject</strong></td></tr>
              <tr><td colspan="4">${formData.subject}</td></tr>
            </table>
            <table class="item-table">
                <thead>
                    <tr>
                        <th>Sr. No.</th><th>SKU</th><th style="width: 40%;">Item & Description</th><th>HSN/SAC</th><th class="text-right">Qty</th><th class="text-right">Rate</th><th class="text-right">Discount</th><th class="text-right">Amount</th>
                    </tr>
                </thead>
                <tbody>
                    ${itemsHtml}
                    <tr><td colspan="8" style="padding: 12px;"></td></tr>
                    <tr class="subtotal-row"><td colspan="5" rowspan="3">Total in Words: <br><strong>${numberToWords(Math.round(totalAmount))}</strong></td><td colspan="2">Sub Total</td><td class="text-right">Rs.${subTotal.toFixed(2)}</td></tr>
                    <tr class="gst-row"><td colspan="2">GST (18%)</td><td class="text-right">Rs.${gstAmount.toFixed(2)}</td></tr>
                    <tr class="total-row"><td colspan="2" style="font-weight:bold;">Total</td><td class="text-right" style="font-weight:bold;">Rs.${totalAmount.toFixed(2)}</td></tr>
                </tbody>
            </table>
            <table class="tnc-table">
                <tr>
                    <td class="terms-conditions"><h4>Terms & Conditions:</h4>${termsHtml}</td>
                    <td class="signature-area">
                      <img src="${selectedFirm.seal}" alt="Seal" style="max-width: 200px; max-height: 150px; marginBottom: 10px;" /><br>
                      <span><strong>For ${selectedFirm.name}<br />Authorized Signatory</strong></span>
                    </td>
                </tr>
            </table>
          </div>
      </body></html>
    `;

    const newWindow = window.open();
    newWindow.document.write(quoteHtml);
    newWindow.document.close();
    newWindow.focus();
    
    newWindow.addEventListener('afterprint', () => {
        newWindow.close();
    }, { once: true });
    
    newWindow.print();
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    if (field === 'name') {
      setActiveSearchRow(index);
      if (value.length > 0) {
        const matches = allProducts.filter(p => p.name?.toLowerCase().includes(value.toLowerCase()) || p.sku?.toLowerCase().includes(value.toLowerCase()));
        setFilteredProducts(matches.slice(0, 10));
      } else {
        setFilteredProducts([]);
      }
    }
    setItems(newItems);
  };
  
  const selectProduct = (index, product) => {
    const newItems = [...items];
    newItems[index] = { 
      ...newItems[index], 
      name: product.name,
      sku: product.sku, 
      description: product.description || 'N/A', 
      hsn: product.sac || 'N/A', 
      rate: product.rate || 0, 
      tax: product.tax || 18 
    };
    setItems(newItems);
    setActiveSearchRow(null);
  };

  const renderPreviewContent = () => {
    const selectedFirm = firms.find(f => f.id === formData.firmId);
    if (!selectedFirm) return <div style={{ padding: '20px', textAlign: 'center' }}><Spinner /></div>;

    const gstTotal = calculateGSTTotal();
    const grandTotal = getGrandTotal();
    const subTotal = calculateSubTotal();
    const formattedQuoteDate = new Date(formData.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    const expiryDate = formData.expiryDate || getDateAfterDays(formData.date, 30);
    const formattedExpiryDate = expiryDate
      ? new Date(`${expiryDate}T00:00:00`).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
      : 'N/A';

    return (
      <div style={{ 
        backgroundColor: 'white', 
        padding: isMobile ? '10px' : '40px', 
        borderRadius: isMobile ? '0' : '10px', 
        boxShadow: isMobile ? 'none' : '0 4px 6px rgba(0,0,0,0.1)', 
        fontFamily: 'Arial, sans-serif', 
        color: '#1b1b1b', 
        maxWidth: '1000px', 
        width: isMobile ? 'calc(100% + 20px)' : '100%',
        margin: isMobile ? '0 -10px' : '0 auto', 
        overflowX: isMobile ? 'auto' : 'visible',
        boxSizing: 'border-box'
      }}>
        <div style={{ minWidth: isMobile ? '800px' : 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #1b1b1b' }}>
            <tbody>
              <tr>
                <td style={{ padding: '10px', verticalAlign: 'middle', borderRight: '1px solid #1b1b1b', width: '20%' }}>
                  <img width="150px" src={selectedFirm.logo} alt="Logo" />
                </td>
                <td style={{ padding: '10px', fontSize: '12px' }}>
                  <strong style={{ fontSize: '20px' }}>{selectedFirm.name}</strong><br />
                  {selectedFirm.address}<br />
                  GSTIN - {selectedFirm.gstin}<br />
                  E-Mail - {selectedFirm.email}<br />
                  Tel: {selectedFirm.tel}
                </td>
                <td style={{ padding: '10px', textAlign: 'right', verticalAlign: 'top' }}>
                  <p style={{ fontSize: '30px', fontWeight: 'bold', margin: '5px 0' }}>QUOTE</p>
                  {formData.quoteNumber}
                </td>
              </tr>
            </tbody>
          </table>

          <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #1b1b1b', borderTop: 'none' }}>
            <tbody>
              <tr>
                <td style={{ padding: '5px', border: '1px solid #1b1b1b' }}><strong>Date:</strong></td>
                <td style={{ padding: '5px', border: '1px solid #1b1b1b' }}>{formattedQuoteDate}</td>
                <td style={{ padding: '5px', border: '1px solid #1b1b1b' }}><strong>Place of supply:</strong></td>
                <td style={{ padding: '5px', border: '1px solid #1b1b1b' }}>{formData.shippingAddress?.state || 'N/A'}</td>
              </tr>
              <tr>
                <td style={{ padding: '5px', border: '1px solid #1b1b1b' }}><strong>Expiry Date:</strong></td>
                <td style={{ padding: '5px', border: '1px solid #1b1b1b' }}>{formattedExpiryDate}</td>
                <td style={{ padding: '5px', border: '1px solid #1b1b1b' }}><strong>Salesperson</strong></td>
                <td style={{ padding: '5px', border: '1px solid #1b1b1b' }}>{formData.salesPerson}</td>
              </tr>
              <tr style={{ backgroundColor: '#f2f2f2', fontWeight: 'bold' }}>
                <td colSpan="2" style={{ padding: '5px', border: '1px solid #1b1b1b' }}>Billing Details:</td>
                <td colSpan="2" style={{ padding: '5px', border: '1px solid #1b1b1b' }}>Shipping Details:</td>
              </tr>
              <tr>
                <td style={{ padding: '5px', border: '1px solid #1b1b1b' }}><strong>Customer</strong></td>
                <td style={{ padding: '5px', border: '1px solid #1b1b1b' }}>{selectedCustomer?.display_name}</td>
                <td style={{ padding: '5px', border: '1px solid #1b1b1b' }}><strong>Ship to:</strong></td>
                <td style={{ padding: '5px', border: '1px solid #1b1b1b' }}>{formData.shippingAddress?.attention || selectedCustomer?.display_name}</td>
              </tr>
              <tr>
                <td style={{ padding: '5px', border: '1px solid #1b1b1b' }}><strong>Address</strong></td>
                <td style={{ padding: '5px', border: '1px solid #1b1b1b' }}>{formatAddress(formData.billingAddress)}</td>
                <td style={{ padding: '5px', border: '1px solid #1b1b1b' }}><strong>Address</strong></td>
                <td style={{ padding: '5px', border: '1px solid #1b1b1b' }}>{formatAddress(formData.shippingAddress)}</td>
              </tr>
              <tr>
                <td style={{ padding: '5px', border: '1px solid #1b1b1b' }}><strong>Contact Person</strong></td>
                <td style={{ padding: '5px', border: '1px solid #1b1b1b' }}>{formData.billingAddress?.attention || 'N/A'}</td>
                <td style={{ padding: '5px', border: '1px solid #1b1b1b' }}><strong>Contact Person</strong></td>
                <td style={{ padding: '5px', border: '1px solid #1b1b1b' }}>{formData.billingAddress?.attention || 'N/A'}</td>
              </tr>
              <tr>
                <td style={{ padding: '5px', border: '1px solid #1b1b1b' }}><strong>Contact Number</strong></td>
                <td style={{ padding: '5px', border: '1px solid #1b1b1b' }}>{formData.billingAddress?.phone || 'N/A'}</td>
                <td style={{ padding: '5px', border: '1px solid #1b1b1b' }}><strong>Contact Number</strong></td>
                <td style={{ padding: '5px', border: '1px solid #1b1b1b' }}>{formData.shippingAddress?.phone || 'N/A'}</td>
              </tr>
              <tr style={{ backgroundColor: '#f2f2f2', fontWeight: 'bold' }}>
                <td colSpan="4" style={{ padding: '5px', border: '1px solid #1b1b1b' }}>Subject</td>
              </tr>
              <tr>
                <td colSpan="4" style={{ padding: '5px', border: '1px solid #1b1b1b' }}>{formData.subject}</td>
              </tr>
            </tbody>
          </table>

          <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #1b1b1b', marginTop: '20px' }}>
            <thead>
              <tr style={{ backgroundColor: '#f9f9f9' }}>
                <th style={{ border: '1px solid #1b1b1b', padding: '5px' }}>Sr.</th>
                <th style={{ border: '1px solid #1b1b1b', padding: '5px' }}>SKU</th>
                <th style={{ border: '1px solid #1b1b1b', padding: '5px', width: '40%' }}>Item</th>
                <th style={{ border: '1px solid #1b1b1b', padding: '5px' }}>HSN</th>
                <th style={{ border: '1px solid #1b1b1b', padding: '5px' }}>Qty</th>
                <th style={{ border: '1px solid #1b1b1b', padding: '5px' }}>Rate</th>
                <th style={{ border: '1px solid #1b1b1b', padding: '5px' }}>Disc%</th>
                <th style={{ border: '1px solid #1b1b1b', padding: '5px' }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, index) => {
                const netAmount = (item.qty * item.rate) * (1 - (item.discount || 0) / 100);
                return (
                  <tr key={index}>
                    <td style={{ border: '1px solid #1b1b1b', padding: '5px' }}>{index + 1}</td>
                    <td style={{ border: '1px solid #1b1b1b', padding: '5px' }}>{item.sku}</td>
                    <td style={{ border: '1px solid #1b1b1b', padding: '5px' }}><strong>{item.name}</strong><br/>{item.description}</td>
                    <td style={{ border: '1px solid #1b1b1b', padding: '5px' }}>{item.hsn}</td>
                    <td style={{ border: '1px solid #1b1b1b', padding: '5px', textAlign: 'right' }}>{item.qty}</td>
                    <td style={{ border: '1px solid #1b1b1b', padding: '5px', textAlign: 'right' }}>{item.rate.toFixed(2)}</td>
                    <td style={{ border: '1px solid #1b1b1b', padding: '5px', textAlign: 'right' }}>{item.discount}%</td>
                    <td style={{ border: '1px solid #1b1b1b', padding: '5px', textAlign: 'right' }}>{netAmount.toFixed(2)}</td>
                  </tr>
                );
              })}
              <tr>
                <td colSpan="6" rowSpan="3" style={{ border: '1px solid #1b1b1b', padding: '5px' }}>
                  Total in Words: <br/><strong>{numberToWords(Math.round(grandTotal))}</strong>
                </td>
                <td style={{ border: '1px solid #1b1b1b', padding: '5px' }}>Sub Total</td>
                <td style={{ border: '1px solid #1b1b1b', padding: '5px', textAlign: 'right' }}>{subTotal.toFixed(2)}</td>
              </tr>
              <tr>
                <td style={{ border: '1px solid #1b1b1b', padding: '5px' }}>GST (18%)</td>
                <td style={{ border: '1px solid #1b1b1b', padding: '5px', textAlign: 'right' }}>{gstTotal.toFixed(2)}</td>
              </tr>
              <tr>
                <td style={{ border: '1px solid #1b1b1b', padding: '5px', fontWeight: 'bold' }}>Total</td>
                <td style={{ border: '1px solid #1b1b1b', padding: '5px', textAlign: 'right', fontWeight: 'bold' }}>{grandTotal.toFixed(2)}</td>
              </tr>
            </tbody>
          </table>

          <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #1b1b1b', marginTop: '20px' }}>
            <tbody>
              <tr>
                <td style={{ padding: '10px', width: '70%', verticalAlign: 'top', fontSize: '12px' }}>
                  <h4 style={{ margin: '0 0 5px 0' }}>Terms & Conditions:</h4>
                  <ol style={{ margin: 0, paddingLeft: '20px' }}>
                    {termsText.split('\n').filter(t => t.trim() !== '').map((t, i) => <li key={i}>{t}</li>)}
                  </ol>
                </td>
                <td style={{ padding: '10px', width: '30%', textAlign: 'right', verticalAlign: 'bottom' }}>
                  <img src={selectedFirm.seal} alt="Seal" style={{ maxWidth: '150px', marginBottom: '10px' }} /><br />
                  <strong>For {selectedFirm.name}</strong><br/>Authorized Signatory
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  if (loading) {
    return <div style={{ padding: '20px' }}><Spinner /></div>;
  }

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', paddingBottom: '100px' }}>
      
      {isSaving && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(255,255,255,0.7)', display: 'flex',
          justifyContent: 'center', alignItems: 'center', zIndex: 9999
        }}>
          <div style={{ textAlign: 'center' }}>
            <Spinner />
            <div style={{ marginTop: '10px', fontWeight: 'bold', color: '#2563eb' }}>Updating Quote...</div>
          </div>
        </div>
      )}

      {isQuoteInvoiced && isEditing && (
        <div style={{ backgroundColor: '#fef3c7', color: '#92400e', padding: '12px', borderRadius: '8px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <FileText size={20} />
          <span>You are not allowed to edit an accepted quote.</span>
        </div>
      )}

      {/* HEADER */}
      <div style={{ 
        display: 'flex', 
        flexDirection: isMobile ? 'column' : 'row', 
        alignItems: isMobile ? 'flex-start' : 'center', 
        justifyContent: 'space-between', 
        marginBottom: '20px', 
        gap: isMobile ? '15px' : '0',
        padding: isMobile ? '0 15px' : '0' 
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Link to="/quotes" style={{ color: '#6b7280' }}><ArrowLeft size={20} /></Link>
          <h2 style={{ margin: 0, fontSize: isMobile ? '20px' : '24px' }}>{isEditing ? 'Edit Quote' : 'Quote Preview'} #{formData.quoteNumber}</h2>
          {!isEditing && (
            <span style={{ 
              padding: '4px 12px', 
              borderRadius: '20px', 
              fontSize: '10px', 
              fontWeight: 'bold',
              textTransform: 'uppercase',
              backgroundColor: formData.status === 'sent' ? '#dcfce7' : formData.status === 'invoiced' ? '#dbeafe' : '#f3f4f6',
              color: formData.status === 'sent' ? '#166534' : formData.status === 'invoiced' ? '#1e40af' : '#374151'
            }}>
              {formData.status}
            </span>
          )}
        </div>
        
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', width: isMobile ? '100%' : 'auto', flexWrap: 'wrap' }}>
          {isEditing ? (
            <>
              <button onClick={() => setIsEditing(false)} style={{ ...btnPrimaryStyle, flex: isMobile ? 1 : 'none', backgroundColor: '#6b7280', justifyContent: 'center' }}>Cancel</button>
              <button onClick={updateQuote} disabled={isQuoteInvoiced} style={{ ...btnPrimaryStyle, flex: isMobile ? 1 : 'none', backgroundColor: isQuoteInvoiced ? '#f3f4f6' : 'white', color: isQuoteInvoiced ? '#9ca3af' : 'black', border: '1px solid #d1d5db', justifyContent: 'center' }}>
                <Save size={18} color={isQuoteInvoiced ? '#9ca3af' : 'black'} /> {isMobile ? 'Update' : 'Update Quote'}
              </button>
            </>
          ) : (
            <>
              {!isQuoteInvoiced && (!isAdmin || (isAdmin && formData.isEscalated)) && (
                <button onClick={() => setIsEditing(true)} style={{ ...btnPrimaryStyle, flex: isMobile ? 1 : 'none', backgroundColor: 'white', color: 'black', border: '1px solid #d1d5db', justifyContent: 'center' }}>
                  <Edit2 size={18} color="black" /> Edit
                </button>
              )}
              {!isAdmin && (
                <button onClick={handleConvertToInvoice} disabled={isQuoteInvoiced} style={{ ...btnPrimaryStyle, flex: isMobile ? 1 : 'none', backgroundColor: isQuoteInvoiced ? '#f3f4f6' : 'white', color: isQuoteInvoiced ? '#9ca3af' : 'black', border: '1px solid #d1d5db', justifyContent: 'center' }}>
                  <CheckCircle size={18} color={isQuoteInvoiced ? '#9ca3af' : 'black'} /> {isMobile ? 'Invoice' : 'Convert to Invoice'}
                </button>
              )}
              <button onClick={handleExportPdf} style={{ ...btnPrimaryStyle, flex: isMobile ? 1 : 'none', backgroundColor: 'white', color: 'black', border: '1px solid #d1d5db', justifyContent: 'center' }}>
                <FileText size={18} color="black" /> {isMobile ? 'PDF' : 'Export PDF'}
              </button>
              
              {!isAdmin && (
                <div style={{ position: 'relative', width: isMobile ? '100%' : 'auto' }} ref={dropdownRef}>
                  <button onClick={() => setShowDropdown(!showDropdown)} style={{ ...btnPrimaryStyle, width: '100%', backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', justifyContent: 'center' }}>
                    More <ChevronDown size={18} />
                  </button>
                  {showDropdown && (
                    <div style={{ ...dropdownStyle, top: 'calc(100% + 5px)', right: 0, left: isMobile ? 0 : 'auto', width: isMobile ? '100%' : '200px', zIndex: 1001 }}>
                      {formData.status === 'draft' && (
                        <div onClick={() => { handleMarkAsSent(); setShowDropdown(false); }} style={dropdownItemStyle}>
                          <FileText size={16} style={{ marginRight: '10px' }} /> Mark as Sent
                        </div>
                      )}
                      {formData.status === 'sent' && (
                        <div onClick={() => { handleUndoSent(); setShowDropdown(false); }} style={dropdownItemStyle}>
                          <X size={16} style={{ marginRight: '10px' }} /> Undo Sent
                        </div>
                      )}
                      <div onClick={() => { handleClone(); setShowDropdown(false); }} style={dropdownItemStyle}>
                        <Copy size={16} style={{ marginRight: '10px' }} /> Clone Quote
                      </div>
                      <div onClick={() => { handleDelete(); setShowDropdown(false); }} style={{ ...dropdownItemStyle, color: '#ef4444' }}>
                        <Trash2 size={16} style={{ marginRight: '10px' }} /> Delete Quote
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {isEditing ? (
        <div style={{ padding: isMobile ? '0 15px' : '0' }}>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 2fr', gap: '20px', marginBottom: '20px' }}>
            <div style={{ ...cardStyle, padding: isMobile ? '15px' : '20px' }}>
               <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr', gap: '15px' }}>
                  <div style={{ gridColumn: isMobile ? 'span 2' : 'auto' }}><label style={labelStyle}><User size={12}/> From (Firm)</label><select name="firmId" value={formData.firmId} onChange={handleFirmChange} style={inputStyle} disabled={isQuoteInvoiced}>{firms.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}</select></div>
                  <div><label style={labelStyle}>Date</label><input type="date" name="date" value={formData.date} onChange={(e) => handleDateChange(e.target.value)} style={inputStyle} readOnly={isQuoteInvoiced} /></div>
                  <div><label style={labelStyle}>Expiry Date</label><input type="date" name="expiryDate" value={formData.expiryDate || ''} min={formData.date || undefined} onChange={(e) => setFormData({...formData, expiryDate: e.target.value})} style={inputStyle} readOnly={isQuoteInvoiced} /></div>
                  <div><label style={labelStyle}>Type</label><select name="quoteType" value={formData.quoteType} onChange={(e) => setFormData({...formData, quoteType: e.target.value})} style={inputStyle} disabled={isQuoteInvoiced}><option>Sales</option><option>Rental</option></select></div>
                  <div style={{ gridColumn: isMobile ? 'span 2' : 'auto' }}><label style={labelStyle}>Salesperson</label><select name="salesPerson" value={formData.salesPerson} style={{ ...inputStyle, backgroundColor: '#f3f4f6', cursor: 'not-allowed' }} disabled><option value={formData.salesPerson || ''}>{formData.salesPerson || 'Auto-filled from profile'}</option></select></div>
                  <div style={{ gridColumn: isMobile ? 'span 2' : 'auto' }}><label style={labelStyle}>Lead Source</label><select name="leadSource" value={formData.leadSource || ''} onChange={(e) => setFormData({...formData, leadSource: e.target.value})} style={inputStyle} disabled={isQuoteInvoiced}><option value="">Select...</option>{leadSourceOptions.map(option => <option key={option} value={option}>{option}</option>)}</select></div>
               </div>
            </div>

            <div style={{ ...cardStyle, padding: isMobile ? '15px' : '20px' }}>
               <div style={{ marginBottom: selectedCustomer ? '20px' : '0' }}>
                  <label style={labelStyle}><User size={12}/> Select Customer</label>
                  <div style={{ position: 'relative' }} ref={customerSearchRef}>
                    <input 
                      placeholder="Search by Display Name..." 
                      value={customerSearchTerm}
                      onFocus={() => { if (!isQuoteInvoiced) { setFilteredCustomers(allCustomers); setShowCustSuggestions(true); }}}
                      onChange={(e) => handleCustomerSearch(e.target.value)}
                      style={{ ...inputStyle, padding: '12px', fontSize: '15px' }}
                      autoComplete="off"
                      readOnly={isQuoteInvoiced}
                    />
                    {showCustSuggestions && !isQuoteInvoiced && (
                      <div style={{ ...dropdownStyle, width: '100%' }}>
                        {filteredCustomers.map(cust => (
                          <div key={cust.id} onClick={() => selectCustomer(cust)} style={{...dropdownItemStyle, display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                            <span><strong>{cust.display_name}</strong> - {cust.company_name}</span>
                            {formData.customerID === cust.id && (
                              <button onClick={(e) => { e.stopPropagation(); setShowModal(true); setCustomerDataToEdit(cust); }} style={linkBtnStyle}>
                                <Edit2 size={12}/> Edit
                              </button>
                            )}
                          </div>
                        ))}
                        <div onClick={() => { setShowModal(true); setCustomerDataToEdit(null); setShowCustSuggestions(false); }} style={{ ...dropdownItemStyle, color: '#2563eb', fontWeight: 'bold', borderTop: '1px solid #e5e7eb', display:'flex', alignItems:'center', gap:5 }}>
                          <Plus size={16} /> Add New Customer
                        </div>
                      </div>
                    )}
                  </div>
               </div>

               {selectedCustomer && (
                 <>
                   <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:5}}>
                     <h4 style={{margin:0, fontSize:'16px'}}>Customer: {selectedCustomer.display_name}</h4>
                     {!isQuoteInvoiced && (
                       <button onClick={() => { setShowModal(true); setCustomerDataToEdit(selectedCustomer); }} style={linkBtnStyle}>
                         <Edit2 size={12}/> Edit Details
                       </button>
                     )}
                   </div>
                   <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '20px', borderTop:'1px solid #e5e7eb', paddingTop:'20px', animation: 'fadeIn 0.3s' }}>
                      <div>
                        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:5}}>
                           <label style={{...labelStyle, marginBottom:0}}><MapPin size={12}/> Billing Address</label>
                           <div>
                             {formData.billingAddress && (
                               <button onClick={() => { setAddressDataToEdit(formData.billingAddress); setShowAddressModal(true); setAddressModalType('billing'); }} style={linkBtnStyle}><Edit2 size={12}/> Edit</button>
                             )}
                           </div>
                        </div>
                        <select 
                          value={formData.billingAddress?.id || 'N/A'} 
                          onChange={(e) => handleAddressSelect('billing', e.target.value)}
                          style={{...inputStyle, borderColor: '#2563eb', backgroundColor:'#eff6ff'}}
                          disabled={isQuoteInvoiced}
                        >
                            <option value="">-- Select Address --</option>
                            {selectedCustomer?.addresses?.filter(a => a.address_type === 'Billing').map(addr => (
                                  <option key={addr.id} value={addr.id}>{addr.attention || addr.city}</option>
                            ))}
                            <option value="new">+ Add New Address</option>
                        </select>
                        {formData.billingAddress && <div style={{...readOnlyBoxStyle, marginTop: '10px'}}>{formatAddress(formData.billingAddress)}</div>}
                          <div style={{marginTop: 15}}>
                              <label style={labelStyle}>Purchaser Details</label>
                              <div style={{fontSize:13, color:'#374151', display:'flex', flexDirection:'column', gap:5}}>
                                {formData.billingAddress?.attention && (
                                  <div style={{display:'flex', gap:5, alignItems:'center', fontWeight:'bold'}}>
                                      <UserCheck size={12} color="#4b5563"/> {formData.billingAddress.attention}
                                  </div>
                                )}
                                <div style={{display:'flex', gap:5, alignItems:'center'}}>
                                    <Phone size={12} color="#4b5563"/> {formData.billingAddress?.phone || selectedCustomer.mobile || '--'}
                                </div>
                                <div style={{display:'flex', gap:5, alignItems:'center'}}>
                                    <Mail size={12} color="#4b5563"/> {formData.billingAddress?.email || 'N/A'}
                                </div>
                             </div>
                          </div>
                      </div>
                      <div>
                        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:5}}>
                           <label style={{...labelStyle, marginBottom:0}}><MapPin size={12}/> Shipping Address</label>
                           <div>
                             {formData.shippingAddress && (
                               <button onClick={() => { setAddressDataToEdit(formData.shippingAddress); setShowAddressModal(true); setAddressModalType('shipping'); }} style={linkBtnStyle}><Edit2 size={12}/> Edit</button>
                             )}
                           </div>
                        </div>
                        <select 
                          value={formData.shippingAddress?.id || 'N/A'} 
                          onChange={(e) => handleAddressSelect('shipping', e.target.value)}
                          style={{...inputStyle, borderColor: '#2563eb', backgroundColor:'#eff6ff'}}
                          disabled={isQuoteInvoiced}
                        >
                            <option value="">-- Select Address --</option>
                            {selectedCustomer?.addresses?.filter(a => a.address_type === 'Shipping').map(addr => (
                                  <option key={addr.id} value={addr.id}>{addr.attention || addr.city}</option>
                            ))}
                            <option value="new">+ Add New Address</option>
                        </select>
                        {formData.shippingAddress && <div style={{...readOnlyBoxStyle, marginTop: '10px'}}>{formatAddress(formData.shippingAddress)}</div>}
                            <div style={{marginTop: 15}}>
                                <label style={labelStyle}>Site Person Details</label>
                                <div style={{fontSize:13, color:'#374151', display:'flex', flexDirection:'column', gap:5}}>
                                  {(formData.shippingAddress?.attention || selectedCustomer.primary_contact?.first_name) && (
                                      <div style={{display:'flex', gap:5, alignItems:'center', fontWeight:'bold'}}>
                                          <UserCheck size={12} color="#4b5563"/> 
                                          {formData.shippingAddress?.attention || `${selectedCustomer.primary_contact?.first_name || 'N/A'} ${selectedCustomer.primary_contact?.last_name || 'N/A'}`}
                                      </div>
                                  )}
                                  <div style={{display:'flex', gap:5, alignItems:'center'}}>
                                      <Phone size={12} color="#4b5563"/> 
                                      {formData.shippingAddress?.phone || selectedCustomer.mobile || selectedCustomer.work_phone || '--'}
                                  </div>
                                  <div style={{display:'flex', gap:5, alignItems:'center'}}>
                                      <Mail size={12} color="#4b5563"/> 
                                      {formData.shippingAddress?.email || '--'}
                                  </div>
                                </div>
                            </div>
                      </div>
                   </div>
                   <div style={{ marginTop: '25px', borderTop:'1px solid #e5e7eb', paddingTop:'20px' }}>
                       <label style={{...labelStyle, fontSize:'12px'}}>Subject</label>
                       <input name="subject" value={formData.subject} onChange={(e) => setFormData(p => ({...p, subject: e.target.value}))} placeholder="Quote Subject..." style={{...inputStyle, padding:'12px', fontSize:'14px', width: '100%'}} readOnly={isQuoteInvoiced} />
                   </div>
                 </>
               )}
            </div>
          </div>

          <div style={{ 
            backgroundColor: 'white', 
            borderRadius: '10px', 
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)', 
            overflowX: isMobile ? 'auto' : 'visible',
            paddingBottom: activeSearchRow !== null ? '150px' : '0',
            transition: 'padding-bottom 0.2s'
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: isMobile ? '800px' : 'auto' }}>
              <thead style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                <tr>
                  <th style={thStyle}>#</th>
                  <th style={{...thStyle, width: '40%'}}>Item Details</th>
                  <th style={{...thStyle, width: '10%'}}>HSN/SAC</th>
                  <th style={thStyle}>Qty</th>
                  <th style={thStyle}>Rate</th>
                  <th style={thStyle}>Discount (%)</th>
                  <th style={thStyle}>Tax (%)</th>
                  <th style={{...thStyle, textAlign: 'right'}}>Amount</th>
                  <th style={thStyle}></th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, index) => {
                  const rowAmount = (item.qty || 0) * (item.rate || 0) * (1 - (item.discount || 0) / 100) * (1 + (item.tax || 0) / 100);
                  return (
                  <tr key={item.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{...tdStyle, textAlign: 'center'}}>{index + 1}</td>
                    <td style={{ ...tdStyle, position: 'relative', textAlign: 'left' }}>
                      <div style={{ position: 'relative', zIndex: activeSearchRow === index ? 100 : 1 }}>
                        <input
                          type="text"
                          value={item.name || ''}
                          onChange={(e) => handleItemChange(index, 'name', e.target.value)}
                          placeholder="Search product by name..."
                          style={{ ...inputStyle, border: 'none', fontWeight: 'bold', padding: '10px 5px' }}
                          readOnly={isQuoteInvoiced}
                        />
                        {item.sku && <div style={{ fontSize: '11px', color: '#6b7280', padding: '0 5px 5px' }}>SKU: {item.sku}</div>}
                        {activeSearchRow === index && filteredProducts.length > 0 && !isQuoteInvoiced && (
                          <div style={{ ...dropdownStyle, width: '100%' }}>
                            {filteredProducts.map(prod => (
                              <div key={prod.id} onClick={() => selectProduct(index, prod)} style={dropdownItemStyle}>
                                <div style={{fontWeight: 'bold', fontSize:12}}>{prod.name} ({prod.sku})</div>
                                <div style={{fontSize:11, color:'#555'}}>{prod.description?.substring(0, 50)}...</div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <textarea
                        value={item.description || ''}
                        onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                        placeholder="Additional description (editable)"
                        style={{ ...inputStyle, border: 'none', resize: 'vertical', marginTop: '5px', padding: '5px' }}
                        rows={2}
                        readOnly={isQuoteInvoiced}
                      />
                    </td>
                    <td style={tdStyle}><input type="text" value={item.hsn} onChange={(e) => handleItemChange(index, 'hsn', e.target.value)} style={tableInputStyle} readOnly={isQuoteInvoiced} /></td>
                    <td style={tdStyle}><input type="number" value={item.qty} onChange={(e) => handleItemChange(index, 'qty', parseFloat(e.target.value))} style={tableInputStyle} readOnly={isQuoteInvoiced} /></td>
                    <td style={tdStyle}><input type="number" value={item.rate} onChange={(e) => handleItemChange(index, 'rate', parseFloat(e.target.value))} style={tableInputStyle} readOnly={isQuoteInvoiced} /></td>
                    <td style={tdStyle}><input type="number" value={item.discount} onChange={(e) => handleItemChange(index, 'discount', parseFloat(e.target.value))} style={tableInputStyle} readOnly={isQuoteInvoiced} /></td>
                    <td style={tdStyle}><input type="number" value={item.tax} onChange={(e) => handleItemChange(index, 'tax', parseFloat(e.target.value))} style={tableInputStyle} readOnly={isQuoteInvoiced} /></td>
                    <td style={{...tdStyle, fontWeight:'bold', textAlign: 'right'}}>₹{rowAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td style={{...tdStyle, textAlign: 'center'}}>{!isQuoteInvoiced && <button onClick={() => setItems(items.filter((_, i) => i !== index))} style={{color:'#ef4444', border:'none', background:'none'}}><Trash2 size={16}/></button>}</td>
                  </tr>
                )})
              }
              </tbody>
            </table>
            <div style={{ padding: '15px', display:'flex', justifyContent:'space-between' }}>
              {!isQuoteInvoiced && <button onClick={() => setItems([...items, { id: Date.now(), name: '', sku:'', description:'', hsn: '', qty:1, rate:0, discount:0, tax:18, total:0 }])} style={{ color:'#2563eb', background:'none', border:'none', cursor:'pointer', fontWeight: 'bold' }}>+ Add Row</button>}
            </div>
          </div>

          <div style={{...cardStyle, marginTop: '20px', padding: isMobile ? '15px' : '20px' }}>
            <h3 style={{ margin: '0 0 15px 0', borderBottom: '1px solid #e5e7eb', paddingBottom: '10px', fontSize: isMobile ? '16px' : '18px' }}>Current Summary</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: isMobile ? '13px' : '15px' }}>
              <div style={{ fontWeight: 'bold' }}>Sub Total (after item discounts):</div>
              <div style={{ textAlign: 'right' }}>Rs.{calculateSubTotal().toLocaleString('en-IN', { maximumFractionDigits: 2 })}</div>
              <div style={{ fontWeight: 'bold' }}>GST:</div>
              <div style={{ textAlign: 'right' }}>Rs.{calculateGSTTotal().toLocaleString('en-IN', { maximumFractionDigits: 2 })}</div>
              <div style={{ fontWeight: 'bold', fontSize: isMobile ? '16px' : '18px', borderTop: '1px solid #e5e7eb', paddingTop: '10px' }}>Grand Total:</div>
              <div style={{ textAlign: 'right', fontWeight: 'bold', fontSize: isMobile ? '16px' : '18px', borderTop: '1px solid #e5e7eb', paddingTop: '10px' }}>Rs.{getGrandTotal().toLocaleString('en-IN', { maximumFractionDigits: 2 })}</div>
            </div>
          </div>
          <div style={{...cardStyle, marginTop: '20px', padding: isMobile ? '15px' : '20px'}}>
            <label style={labelStyle}>Terms & Conditions (Editable)</label>
            <textarea
                value={termsText}
                onChange={(e) => setTermsText(e.target.value)}
                style={{...inputStyle, width: '100%', minHeight: '120px', resize: 'vertical', fontSize: '13px'}}
                placeholder="Enter terms and conditions here (one per line)."
                readOnly={isQuoteInvoiced}
            />
          </div>
        </div>
      ) : (
        <>
          {renderPreviewContent()}
          <div style={{ ...cardStyle, marginTop: '20px', padding: isMobile ? '15px' : '20px' }}>
            <h3 style={{ margin: '0 0 15px 0', borderBottom: '1px solid #e5e7eb', paddingBottom: '10px', fontSize: isMobile ? '16px' : '18px' }}>
              More Information
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '180px 1fr', gap: '10px', fontSize: isMobile ? '13px' : '15px' }}>
            <div style={{ fontWeight: 'bold' }}>Created at</div>
            <div>{moreInfoCreatedAt}</div>
              <div style={{ fontWeight: 'bold' }}>Created By</div>
              <div>{moreInfoCreatedBy}</div>
              <div style={{ fontWeight: 'bold' }}>Updated at</div>
              <div>{moreInfoUpdatedAt}</div>
              <div style={{ fontWeight: 'bold' }}>Updated by</div>
              <div>{moreInfoUpdatedBy}</div>
              <div style={{ fontWeight: 'bold' }}>Lead Source</div>
              <div>{moreInfoLeadSource}</div>
            </div>
          </div>
        </>
      )}

      <NewCustomerModal 
        show={showModal} 
        onClose={() => { setShowModal(false); setCustomerDataToEdit(null); }} 
        onSave={(savedCust) => {
          setAllCustomers(prev => {
            const existingIndex = prev.findIndex(c => c.id === savedCust.id);
            if (existingIndex > -1) {
              return prev.map(c => c.id === savedCust.id ? savedCust : c);
            }
            return [...prev, savedCust];
          });
          selectCustomer(savedCust);
        }}
        db={db}
        customerData={customerDataToEdit}
        isMobile={isMobile}
        isEditingAllowed={isEditingAllowed && !isQuoteInvoiced} 
      />

      <AddAddressModal
        show={showAddressModal}
        onClose={() => { setShowAddressModal(false); setAddressDataToEdit(null); }}
        onSave={(savedAddress, updatedAddresses) => {
          const updatedCustomer = { ...selectedCustomer, addresses: updatedAddresses };
          setAllCustomers(prev => prev.map(c => c.id === updatedCustomer.id ? updatedCustomer : c));
          if (savedAddress.address_type === 'Billing') {
            setFormData(prev => ({ ...prev, billingAddress: savedAddress }));
          } else {
            setFormData(prev => ({ ...prev, shippingAddress: savedAddress }));
          }
        }}
        db={db}
        customer={selectedCustomer}
        addressType={addressModalType}
        addressData={addressDataToEdit} 
        isMobile={isMobile}
        isEditingAllowed={isEditingAllowed && !isQuoteInvoiced} 
      />
    </div>
  );
};

// --- STYLES ---
const cardStyle = { backgroundColor: 'white', padding: '20px', borderRadius: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' };
const btnPrimaryStyle = { backgroundColor: '#2563eb', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' };
const labelStyle = { display: 'block', fontSize: '11px', fontWeight: '600', color: '#6b7280', marginBottom: '4px', textTransform: 'uppercase' };
const inputStyle = { width: '100%', padding: '8px', borderRadius: '5px', border: '1px solid #d1d5db', fontSize: '13px', boxSizing: 'border-box' };
const dropdownStyle = { position: 'absolute', top: '100%', left: 0, width: '100%', maxHeight: '250px', overflowY: 'auto', backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '6px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', zIndex: 100 };
const dropdownItemStyle = { padding: '10px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6' };
const readOnlyBoxStyle = { background: '#f8fafc', padding: '10px', borderRadius: '5px', border: '1px solid #e5e7eb', fontSize: '13px', color: '#374151', minHeight: '40px' };
const linkBtnStyle = { background:'none', border:'none', color:'#2563eb', cursor:'pointer', fontSize:'12px', display:'flex', alignItems:'center', gap:4, padding:0 };
const modalOverlayStyle = { position: 'fixed', top:0, left:0, right:0, bottom:0, backgroundColor: 'rgba(0,0,0,0.5)', display:'flex', justifyContent:'center', alignItems:'center', zIndex: 1000 };
const modalContentStyle = { backgroundColor: 'white', width: '900px', maxWidth:'95%', borderRadius: '8px', padding: '20px', display:'flex', flexDirection:'column', position: 'relative' };
const rowStyle = { display: 'flex', gap: '15px', marginBottom: '15px' };
const sectionHeaderStyle = { margin: '20px 0 10px 0', borderBottom:'1px solid #eee', paddingBottom:5, color:'#374151', fontSize: '14px', fontWeight:'bold' };
const tableInputStyle = { width: '100%', padding: '5px', border: '1px solid #d1d5db', textAlign: 'center', fontSize: '13px', borderRadius: '4px' };
const thStyle = { padding: '12px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: '#374151', textTransform: 'uppercase' };
const tdStyle = { padding: '10px', fontSize: '13px', textAlign: 'center' };

export default QuoteDetail;
